/**
 * PreJoiningPortal — public magic-link page for a new hire.
 *
 * Route: /welcome/:token  (no auth required)
 *
 * Shows countdown, team faces, manager+buddy, welcome video, and lets the
 * hire "say hi" (posted back to HR / manager notifications).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PartyPopper, Sparkles, Users, MessageCircle, PlayCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function PreJoiningPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/welcome/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invalid link"));
  }, [token]);

  // Live countdown ticker — uses server-provided workspace-TZ anchor when available,
  // so hires joining offices in different TZs see the correct "Day 1 morning" countdown.
  useEffect(() => {
    if (!data?.joining_date && !data?.joining_at_iso) return;
    const tick = () => {
      const target = data.joining_at_iso
        ? new Date(data.joining_at_iso)
        : new Date(data.joining_date + "T09:00:00");
      const diff = target - new Date();
      if (diff <= 0) { setCountdown({ d: 0, h: 0, m: 0, s: 0, elapsed: true }); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown({ d: days, h: hours, m: mins, s: secs, elapsed: false });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [data?.joining_date, data?.joining_at_iso]);

  const sayHi = async () => {
    if (!msg.trim()) return;
    try {
      await axios.post(`${API}/public/welcome/${token}/say-hi`, { message: msg });
      setSent(true);
      toast.success("Your message is on its way to the team!");
    } catch (e) { toast.error("Could not send — please try again"); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="portal-err">
      <div className="max-w-md text-center">
        <div className="text-6xl mb-3">🥲</div>
        <h1 className="text-2xl font-bold mb-2">This link isn&apos;t working</h1>
        <p className="text-sm text-muted-foreground">{err}. Please reach out to your HR contact for a fresh link.</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading your welcome…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary/5 dark:from-slate-950 dark:to-slate-900" data-testid="pre-joining-portal">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="mb-12">
          <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Welcome aboard
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold leading-tight tracking-tight" data-testid="portal-greeting">
            Hi {(data.employee?.full_name || "there").split(" ")[0]}!<br />
            <span className="text-primary">We can&apos;t wait to meet you.</span>
          </h1>
          <p className="text-lg text-muted-foreground mt-4">
            You&apos;re joining <b>{data.workspace_name}</b> as <b>{data.employee?.designation || "our newest teammate"}</b>
            {data.employee?.department ? <> in {data.employee.department}</> : null}.
          </p>
        </div>

        {/* Countdown */}
        {countdown && !countdown.elapsed && (
          <div className="border-2 border-primary/40 bg-primary/5 p-6 mb-10" data-testid="portal-countdown">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Time until Day 1 · {data.joining_date}{data.workspace_timezone ? ` · ${data.workspace_timezone}` : ""}</div>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "days", v: countdown.d, testid: "cd-days" },
                { label: "hours", v: countdown.h, testid: "cd-hours" },
                { label: "minutes", v: countdown.m, testid: "cd-minutes" },
                { label: "seconds", v: countdown.s, testid: "cd-seconds" },
              ].map((t) => (
                <div key={t.label} className="text-center border border-border bg-background p-3" data-testid={t.testid}>
                  <div className="text-4xl sm:text-5xl font-bold font-mono">{String(t.v).padStart(2, "0")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {countdown?.elapsed && (
          <div className="border-2 border-emerald-500/40 bg-emerald-500/10 p-6 mb-10 flex items-center gap-3" data-testid="portal-day-here">
            <PartyPopper className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-bold text-lg">Day 1 is here!</div>
              <div className="text-sm text-muted-foreground">Your team is expecting you. Break a leg 🎯</div>
            </div>
          </div>
        )}

        {/* Manager + Buddy */}
        {(data.manager || data.buddy) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {data.manager && (
              <PersonCard label="Your manager" person={data.manager} testid="portal-manager" />
            )}
            {data.buddy && (
              <PersonCard label="Your buddy" person={data.buddy} testid="portal-buddy" />
            )}
          </div>
        )}

        {/* Welcome video */}
        {data.welcome_video_url && (
          <div className="mb-10" data-testid="portal-video">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><PlayCircle className="w-3 h-3" /> Welcome message</div>
            <div className="aspect-video border border-border">
              <iframe src={data.welcome_video_url} className="w-full h-full" allow="fullscreen" title="Welcome video" />
            </div>
          </div>
        )}

        {/* Team */}
        {data.team?.length > 0 && (
          <div className="mb-10" data-testid="portal-team">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Users className="w-3 h-3" /> Your team can&apos;t wait</div>
            <div className="flex flex-wrap gap-3">
              {data.team.map((t) => (
                <div key={t.id} className="flex items-center gap-2 border border-border bg-background px-3 py-1.5">
                  {t.avatar_data_url ? (
                    <img src={t.avatar_data_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                      {(t.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm">{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Say hi */}
        <div className="border-2 border-border p-5" data-testid="portal-sayhi">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            <MessageCircle className="w-3 h-3" /> Say hi — we&apos;ll share it with your team
          </div>
          {sent ? (
            <div className="text-sm text-emerald-700" data-testid="portal-sayhi-done">✓ Your message is on its way. See you soon!</div>
          ) : (
            <div className="flex gap-2">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="A quick hello, an intro, or a fun fact about yourself…" className="h-10 rounded-none" data-testid="portal-sayhi-input" />
              <Button onClick={sayHi} className="rounded-none h-10" disabled={!msg.trim()} data-testid="portal-sayhi-send">
                <Send className="w-3.5 h-3.5 mr-1" /> Send
              </Button>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-muted-foreground mt-12">
          — {data.workspace_name}
        </div>
      </div>
    </div>
  );
}

function PersonCard({ label, person, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
      <div className="flex items-center gap-3">
        {person.avatar_data_url ? (
          <img src={person.avatar_data_url} alt="" className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary">
            {(person.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-semibold">{person.name}</div>
          {person.email && <div className="text-xs text-muted-foreground">{person.email}</div>}
        </div>
      </div>
    </div>
  );
}
